<template>
  <div class="fts-console-element">
    <draggable
      v-model="canvas[item]"
      v-bind="dragOptions"
      handle=".console-element-item__handle"
      draggable=".console-element-item-draggable"
      :disabled="!draggable"
      @start="onDrag = true"
      @end="onDrag = false"
    >
      <div
        class="console-element-item-draggable"
        v-for="elementItem in filterElement"
        :key="`${elementItem.id}New`"
      >
        <div
          class="console-element-item"
          @click="selectConsoleElement(elementItem)"
          v-if="elementItem"
        >
          <!-- handle -->
          <font-awesome-icon
            :icon="['fas', 'grip-vertical']"
            class="console-element-item__handle"
            v-if="draggable"
          />
          <!-- content -->
          <div class="console-element-item__content">
            <!-- wrapper -->
            <div class="console-element-item__content-wrapper">
              <!-- info -->
              <div class="console-element-info">
                <!-- emoji -->
                <fts-moji
                  :emoji="elementItem.emoji"
                  :size="16"
                  class="console-element-emoji"
                  v-if="elementItem.emoji"
                />
                <!-- information icon -->
                <fts-tippy
                  :content="elementItem.displayName"
                  :options="{ delay: tippyDelay }"
                  v-if="item === 'information'"
                >
                  <div slot="trigger" class="console-element-icon">
                    <i
                      class="info-icon"
                      :class="[
                        informationIcon(elementItem.type), {'console-element-icon--system': system}
                      ]"
                    />
                  </div>
                </fts-tippy>
                <!-- document icon -->
                <template v-if="item === 'documents'">
                  <fts-tippy :options="{ delay: tippyDelay }">
                    <template #trigger>
                      <font-awesome-icon
                        class="console-element-fa fts-file-pdf"
                        :icon="['far', 'file-pdf']"
                        v-if="elementItem.type === 'PDF'"
                      />
                      <font-awesome-icon
                        class="console-element-fa fts-file-excel"
                        :icon="['far', 'file-alt']"
                        v-else-if="elementItem.type === 'Spreadsheet'"
                      />
                      <font-awesome-icon
                        class="console-element-fa fts-file-html"
                        :icon="['far', 'file-code']"
                        v-else-if="elementItem.type === 'HTML'"
                      />
                      <font-awesome-icon
                        class="console-element-fa fts-file-default"
                        :icon="['far', 'file']"
                        v-else
                      />
                    </template>
                    <template #content>
                      <span v-if="elementItem.type === 'PDF'">PDF</span>
                      <span v-else-if="elementItem.type === 'File'">File</span>
                      <span v-else-if="elementItem.type === 'Spreadsheet'">
                        Spreadsheet
                      </span>
                      <span v-else>HTML</span>
                    </template>
                  </fts-tippy>
                </template>
                <!-- state -->
                <fts-state
                  :state="{
                    id: elementItem.id,
                    name: elementItem.name || '(undefined)',
                    color: elementItem.color,
                  }"
                  class="console-element-state"
                  v-if="item === 'states'"
                />
                <!-- flow -->
                <div class="console-element-flow" v-else-if="item === 'flows'">
                  <!-- from state -->
                  <fts-state
                    :state="{
                      name: getState(elementItem.fromStateId).name,
                      color: getState(elementItem.fromStateId).color,
                    }"
                    class="flow-state"
                    v-if="
                      elementItem.fromStateId && getState(elementItem.fromStateId)
                    "
                  />
                  <fts-state
                    :state="{ name: '(any state)' }"
                    class="flow-state"
                    v-else
                  />
                  <!-- direction -->
                  <font-awesome-icon
                    :icon="['fas', 'long-arrow-alt-right']"
                    class="flow-direction"
                  />
                  <!-- activity -->
                  <div
                    class="flow-activity"
                    v-if="getActivity(elementItem.activityId)"
                  >
                    <fts-moji
                      :size="15"
                      :emoji="getActivity(elementItem.activityId).emoji"
                      class="flow-activity-emoji"
                      v-if="getActivity(elementItem.activityId).emoji"
                    />
                    <div
                      class="flow-activity-name"
                      :style="{
                        color: getActivity(elementItem.activityId).color,
                      }"
                    >
                      {{ getActivity(elementItem.activityId).name }}
                    </div>
                  </div>
                  <div class="flow-no-activity" v-else>(no activity)</div>
                  <!-- direction -->
                  <font-awesome-icon
                    :icon="['fas', 'long-arrow-alt-right']"
                    class="flow-direction"
                  />
                  <!-- to state -->
                  <template
                    v-if="
                      elementItem.conditional &&
                      elementItem.conditionalStates &&
                      elementItem.conditionalStates.length
                    "
                  >
                    <template
                      v-for="(state, stateIndex) in elementItem.conditionalStates"
                    >
                      <div
                        :key="`${state.id}-or`"
                        class="flow-conditional"
                        v-if="stateIndex"
                      >
                        or
                      </div>
                      <fts-state
                        :key="state.id"
                        :state="{
                          name: getState(state.toStateId).name,
                          color: getState(state.toStateId).color,
                        }"
                        class="flow-state"
                        v-if="getState(state.toStateId)"
                      />
                    </template>
                  </template>
                  <fts-state
                    :state="{
                      name: getState(elementItem.toStateId).name,
                      color: getState(elementItem.toStateId).color,
                    }"
                    class="flow-state"
                    v-else-if="
                      elementItem.toStateId && getState(elementItem.toStateId)
                    "
                  />
                  <fts-state
                    :state="{ name: '(any state)' }"
                    class="flow-state"
                    v-else
                  />
                </div>
                <!-- name -->
                <div
                  class="console-element-name"
                  :style="{ color: elementItem.color }"
                  v-else
                >
                  {{elementName(elementItem)}}
                </div>
              </div>
              <!-- module -->
              <fts-label-group 
                :item="getModule(elementItem.connectionId)"
                emoji
                :emoji-size="12"
                class="console-element-badge"
                v-if="elementItem.connectionId && getModule(elementItem.connectionId)"
              />
              <!-- system (information) -->
              <fts-tippy
                trigger-class="console-element-badge--icon-wrapper"
                class="console-element-badge-tippy"
                v-if="type === 'information' && system"
              >
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="console-element-badge console-element-badge--icon"
                />
                <div slot="content">
                  {{elementItem.description}}
                </div>
              </fts-tippy>
              <!-- standard activity -->
              <fts-tippy
                trigger-class="console-element-badge--icon-wrapper"
                class="console-element-badge-tippy"
                v-if="type === 'standardActivity' && getDescription(elementItem)"
              >
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="console-element-badge console-element-badge--icon"
                />
                <div slot="content">
                  {{getDescription(elementItem)}}
                </div>
              </fts-tippy>
              <!-- standard:initial -->
              <fts-btn
                sm
                color="secondary"
                text-transform-init
                box-shadow-none
                class="console-element-btn"
                data-v-step="studio-tour-start"
                v-if="
                  type === 'standardActivity' && 
                  elementItem.id === 'createEditView' &&
                  (canvas.information && canvas.information.length === 0)
                "
              >
                Start here
              </fts-btn>
              <!-- classification -->
              <fts-tippy
                :options="{
                  interactive: true,
                  placement: 'left-start',
                  delay: [500 , 0],
                  theme: 'light-border',
                }"
                popper-prevent-overflow
                :key="isClassification(elementItem)"
                trigger-class="console-element-badge--icon-wrapper"
                class="console-element-badge-tippy"
                v-else-if="isClassification(elementItem)"
              >
                <i
                  class="
                    fts-icon-form
                    console-element-badge console-element-badge--icon
                    console-element-badge--primary
                  "
                  slot="trigger"
                />
                <div slot="content" class="form-preview">
                  <div class="form-preview__header">
                    Information for {{elementItem.name}}
                  </div>
                  <div class="form-preview__content fts-scrollbar">
                    <template v-if="classificationInformation(elementItem) && classificationInformation(elementItem).length">
                      <div
                        v-for="x in classificationInformation(elementItem)"
                        :key="x.id"
                        class="form-preview-information"
                      >
                        <i class="form-preview-information-icon" :class="informationIcon(x.type)" />
                        <div>
                          {{x.displayName}}
                        </div>
                      </div>
                    </template>
                    <div class="form-preview-blank" v-else>
                      User-defined form.
                    </div>
                  </div>
                </div>
              </fts-tippy>
              <!-- classification:auto -->
              <fts-tippy
                class="console-element-badge-tippy"
                v-else-if="elementItem.id == 'createEditView'"
              >
                <div class="console-element-badge" slot="trigger" >
                  auto
                </div>
                <div slot="content">
                  <div>Auto-generated form based on information</div>
                </div>
              </fts-tippy>
              <!-- classification:default -->
              <fts-tippy
                class="console-element-badge-tippy"
                v-else-if="
                  elementItem.id == 'create' ||
                  elementItem.id == 'edit' ||
                  elementItem.id == 'view' ||
                  elementItem.id == 'quickView'
                "
              >
                <div class="console-element-badge" slot="trigger" >
                  default
                </div>
                <div slot="content">
                  <div>Using default form</div>
                </div>
              </fts-tippy>
              <!-- internal -->
              <div class="console-element-badge" v-if="elementItem.internal">
                internal
              </div>
              <!-- skip history -->
              <div class="console-element-badge" v-if="elementItem.skipHistory">
                skip history
              </div>
              <!-- initial -->
              <div class="console-element-badge" v-if="elementItem.initial">
                initial
              </div>
              <!-- data source -->
              <div class="console-element-badge fts-lowercase" v-if="elementItem.dataSource">
                {{ elementItem.dataSource }}
              </div>
              <!-- list -->
              <div
                class="console-element-list"
                v-if="Boolean(elementItem.menus && elementItem.menus.length)"
              >
                <div class="console-element-list-container">
                  <template v-for="(menu, ind) in elementItem.menus">
                    <div class="console-element-list-wrapper" :key="`menu-${menu}-${ind}`">
                      <fts-label-group
                        :item-id="menu"
                        :list="menus"
                        emoji
                        :emoji-size="12"
                      />
                      <span v-if="ind !== elementItem.menus.length - 1">,</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <!-- action -->
            <div class="console-element-item__content-action">
              <template v-if="automation">
                <!-- notification:action -->
                <fts-event-toggle
                  event-type="notification"
                  class="console-element-automation-action stopPropagation"
                  @open="addAutomation(elementItem, 'notification')"
                />
                <!-- automation:action -->
                <fts-event-toggle
                  class="console-element-automation-action stopPropagation"
                  @open="addAutomation(elementItem, 'automation')"
                />
              </template>
              <!-- authorization:import -->
              <fts-auth
                type="import"
                :title="elementName(elementItem)"
                class="console-element-authorization stopPropagation"
                v-if="elementItem.id === 'import' && authorization"
              />
              <!-- authorization:default -->
              <fts-auth
                :unitId="elementItem.id"
                :type="authorizationType"
                :title="elementName(elementItem)"
                class="console-element-authorization stopPropagation"
                v-else-if="authorization"
              />
              <!-- actions -->
              <fts-dropdown-select
                placement="left-start"
                strategy-fixed
                prevent-overflow-tether-off
                :prevent-overflow-boundary="parentId"
                :offset="[0, 6]"
                append-content-class="ce-content ce-dropdown-menu"
                :z-index="10"
                class="console-element-actions stopPropagation"
                @selected="executeAction(elementItem, $event)">
                <font-awesome-icon
                  slot="toggle"
                  class="console-element-action-trigger"
                  :icon="['fas', 'ellipsis-v']"
                  fixed-width
                />
                <template #menu="{select, defaultOptionClass}">
                  <template v-if="system">
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')">
                      <font-awesome-icon :icon="['far', 'file']" class="ce-action-option-icon" /> View
                    </div>
                  </template>
                  <template v-else>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')"
                    >
                      <font-awesome-icon :icon="['far', 'edit']" class="ce-action-option-icon" /> Edit
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('delete')"
                      v-if="editable"
                    >
                      <font-awesome-icon :icon="['far', 'trash-alt']" class="ce-action-option-icon" /> Delete
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('duplicate')"
                      v-if="editable"
                    >
                      <font-awesome-icon :icon="['far', 'clone']" class="ce-action-option-icon" /> Duplicate
                    </div>
                  </template>
                </template>
              </fts-dropdown-select>
            </div>
          </div>
          <!-- automation -->
          <div class="console-element-item__automation">
            <div
              class="console-element-item__automation-container"
              v-if="
                getAutomation(elementItem.id) &&
                getAutomation(elementItem.id).length
              "
            >
              <!-- <fts-event
                :event="{ id: 'Subscription', count: 4, name: 'Subscription' }"
                :state-indicator="false"
                class="stopPropagation"
                @open="openSubscription(elementItem)"
                v-if="isUIDesigner"
              /> -->
              <fts-event
                v-for="event in getAutomation(elementItem.id)"
                :key="event.id"
                :event="event"
                class="stopPropagation"
                @open="openAutomation"
              />
            </div>
          </div>
        </div>
        <!-- information fields -->
        <template
          v-if="
            item === 'information' &&
            elementItem &&
            elementItem.fields &&
            elementItem.fields.length &&
            !onDrag
          "
        >
          <div
            class="console-element-item console-element-item-child"
            v-for="column in elementItem.fields"
            :key="`${column.id}`"
            @click="selectConsoleElement(column)"
          >
            <!-- content -->
            <div class="console-element-item__content">
              <!-- wrapper -->
              <div class="console-element-item__content-wrapper">
                <!-- info -->
                <div class="console-element-info">
                  <!-- emoji -->
                  <fts-moji
                    :emoji="elementItem.emoji"
                    :size="16"
                    class="console-element-emoji"
                    v-if="elementItem.emoji"
                  />
                  <!-- information icon -->
                  <fts-tippy
                    :content="column.displayName"
                    :options="{ delay: tippyDelay }"
                    v-if="item === 'information'"
                  >
                    <div slot="trigger" class="console-element-icon">
                      <i
                        class="info-icon"
                        :class="informationIcon(column.type)"
                      />
                    </div>
                  </fts-tippy>
                  <!-- name -->
                  <div class="console-element-name">
                    {{ column.displayName }}
                  </div>
                </div>
                <!-- module -->
                <fts-label-group 
                  :item="getModule(column.connectionId)"
                  emoji
                  :emoji-size="12"
                  class="console-element-badge"
                  v-if="column.connectionId && getModule(column.connectionId)"
                />
              </div>
              <!-- action -->
              <div class="console-element-item__content-action">
                <!-- actions -->
                <fts-dropdown-select
                  placement="left-start"
                  strategy-fixed
                  prevent-overflow-tether-off
                  :prevent-overflow-boundary="parentId"
                  :offset="[0, 6]"
                  append-content-class="ce-content ce-dropdown-menu"
                  :z-index="10"
                  class="console-element-actions stopPropagation"
                  @selected="executeAction(column, $event, elementItem.id)">
                  <font-awesome-icon
                    slot="toggle"
                    class="console-element-action-trigger"
                    :icon="['fas', 'ellipsis-v']"
                    fixed-width
                  />
                  <template #menu="{select, defaultOptionClass}">
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')">
                      <font-awesome-icon :icon="['far', 'edit']" class="ce-action-option-icon" /> Edit
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('delete')"
                      v-if="editable">
                      <font-awesome-icon :icon="['far', 'trash-alt']" class="ce-action-option-icon" /> Delete
                    </div>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
          </div>
        </template>
      </div>
    </draggable>
    <!-- action:load more -->
    <div class="console-element-footer" v-if="pageSize && filterElement.length >= pageSize">
      <fts-btn
        text
        text-transform-init
        full-width
        class="fts-font-normal"
        @click="loadMore = !loadMore" 
      >
        {{loadMore ? "show less" : "show more"}}
      </fts-btn>
    </div>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import commons from "../commons/commons";

import draggable from "vuedraggable";

import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSMoji from "../elements/FTSMoji";
import FTSState from "../elements/FTSState";
import FTSTippy from "../elements/FTSTippy";
import FTSAuth from "../design/FTSAuth";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSBtn from "../elements/FTSBtn";

import FTSEvent from "./event/FTSEvent";
import FTSEventToggle from "./event/FTSEventToggle";

export default {
  props: {
    parentId: String,
    canvas: Object,
    item: String,
    type: String,
    label: String,
    pageSize: Number,
    draggable: {
      type: Boolean,
      default: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    authorization: {
      type: Boolean,
      default: true,
    },
    automation: {
      type: Boolean,
      default: true,
    },
    system: Boolean,
  },
  components: {
    draggable,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-moji": FTSMoji,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-auth": FTSAuth,
    "fts-label-group": FTSLabelGroup,
    "fts-btn": FTSBtn,
    "fts-event": FTSEvent,
    "fts-event-toggle": FTSEventToggle,
  },
  data: () => ({
    dragOptions: commons.dragOptions,
    onDrag: false,
    tippyDelay: [1000, 0],
    loadMore: false,
  }),
  methods: {
    getDescription(item) {
      switch(item.id) {
        case 'createEditView':
          return 'Default form if create, edit, quick view or view does not have form. If form is not set it will automatically generated based on Information';

        case 'create':
          return 'To create or add new entry';

        case 'update':
          return 'To update or edit an entry';

        case 'quickView':
          return 'To quick view an entry';

        case 'view':
          return 'To view an entry';

        case 'delete':
          return 'To delete an entry';

        case 'changeStatus':
          return 'To change state of an entry';

        case 'duplicate':
          return 'To make a copy of an entry';

        case 'history':
           return 'To view history of an entry';

        case 'comment':
          return 'To comment on an entry';

        case 'import':
          return 'To import or merge an entry using spreadsheet';
      }
    },
    selectConsoleElement(data) {
      if (commons.elementStopPropagation()) return;
      this.update(data);
    },
    update(data) {
      this.$emit("update", data);
    },
    /* automations */
    getAutomation(id) {
      return _.sortBy(
        this.$store.getters.vectorTriggers.filter(x => x.item == id),
        ['actionType']
      )
    },
    getAutomationLabel(item) {
      if (item.description)
        return item.description;
      else if (
        item.notification || /* item.notification => old */
        item.actionType === 'notification'
      )
        return 'Notification';
      else if (item.actionType == 'advanced')
        return 'Advanced';
      else if (item.actionType == 'automation')
        return 'Automation';
      else
        return '(undefined type)';
    },
    openAutomation(item) {
      if (!item.type)
        item.type = this.type;

      this.$emit("openAutomation", item);
    },
    addAutomation(element, actionType) {
      this.openAutomation({
        item: element.id,
        type: this.type,
        trigger: "execute",
        actionType: actionType,
      });
    },
    duplicate(data) {
      let cloned = _.cloneDeep(data);
      if (this.type === "information") {
        function generateNewIdentity(field) {
          field.id = commons.generateFieldName();
          field.fieldName = commons.generateFieldName();
        }

        generateNewIdentity(cloned);
        cloned.displayName = `${cloned.displayName} (Copy)`;
  
        if ((cloned.type === 14 || cloned.type === 15) && Array.isArray(cloned.fields)) {
          cloned.fields.forEach(x => {
            generateNewIdentity(x);
            if (x.type === 14 && Array.isArray(x.fields)) {
              x.fields.forEach(y => {
                generateNewIdentity(y);
              });
            }
          });
        }
      } else {
        cloned.id = this.type === "menu" ? commons.nanoID() : commons.newUUID();
        if (this.type !== "flow")
          cloned.name = `${cloned.name} (Copy)`;
      }
      this.canvas[this.item].push(cloned);
    },
    deleteItem(data, parentId) {
      let buttons = [{ title: "Cancel" }];
      buttons.push({
        title: "Delete",
        default: true,
        handler: () => {
          // Information Column(s)
          if (parentId) {
            try {
              let parent = this.canvas.information.find(
                (x) => x.id === parentId
              );
              parent.fields.splice(parent.fields.indexOf(data), 1);
            } catch (err) {
              console.warn(err);
            }
          } else {
            this.canvas[this.item].splice(
              this.canvas[this.item].indexOf(data),
              1
            );
          }
          this.$modal.hide("dialog");
        },
      });
      let text = `Delete ${data.name || data.displayName}?`;
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: text,
        buttons: buttons,
      });
    },
    /* subscription */
    openSubscription(item) {
      this.$emit("openSubscription", item);
    },
    /* end subscription */
    elementName(item) {
      if (this.type === 'information')
        return item.displayName;
      else if (this.type === 'standardActivity' && item.id === 'createEditView')
        return 'Default (Create + Edit + View)';
      else
        return item.name;
    },
    informationIcon(type) {
      return commons.informationIcon(type);
    },
    getModule(id) {
      if (id && this.$store.getters.workspaceModules) {
        const findModule = this.$store.getters.workspaceModules.find((x) => x.id == id);
        if (findModule) 
          return {
            id: findModule.id,
            name: findModule.name,
            emoji: findModule.emoji,
          }
      }
    },
    getState(id) {
      if (this.canvas.states)
        return this.canvas.states.find((x) => x.id === id);
    },
    getActivity(id) {
      if (this.canvas.activities)
        return this.canvas.activities.find((x) => x.id === id);

      return {};
    },
    selectModule(moduleId) {
      this.$emit("moduleSelected", {
        id: moduleId,
      });
    },
    executeAction(data, type, parentId) {
      if (type === 'duplicate')
        this.duplicate(data)
      else if (type === 'delete')
        this.deleteItem(data, parentId)
      else
        this.update(data)
    },
    classificationInformation(item) {
      if (this.type === 'document' && item.hasUserInput && item.userInput && item.userInput.rows)
        return commons.classificationInformation(item.userInput.rows, this.information); 
      else if (item.design && item.design.rows)
        return commons.classificationInformation(item.design.rows, this.information);

      return [];
    },
    isClassification(item) {
      return Boolean(
        ((this.type === 'activity' || this.type == 'standardActivity') && item.design) ||
        (this.type === 'document' && item.hasUserInput)
      );
    },
  },
  watch: {
    searchKeyword: function () {},
  },
  computed: {
    beta() {
      return this.$store.state.beta;
    },
    isUIDesigner() {
      return authorizationStore.getters.isUIDesigner;
    },
    searchKeyword() {
      return this.$store.state.filterKeyword;
    },
    authorizationType() {
      switch (this.item) {
        case "information":
          return "information";
        case "activities":
        case "standardActivities":
          return "activity";
        case "states":
          return "state";
        // case 'flows':
        //   return 'flow'
        case "menus":
          return "menu";
        // case 'documents':
        //   return 'document'
        // case 'actions':
        //   return 'action'
        // case 'logicServices':
        //   return 'logicService'
      }
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    menus() {
      return this.$store.state.canvas.menus || [];
    },
    filterElement() {
      let results = commons.isArray(this.canvas[this.item]) ? [...this.canvas[this.item]] : [];

      if (!this.beta && this.type === "standardActivity") {
        results = results.filter(x => x.id !== 'print');
      }

      if (
        this.searchKeyword &&
        !this.label.toLowerCase().indexOf(this.searchKeyword) > -1
      ) {
        results = results.filter(x =>
          (
            x.displayName && x.displayName.toLowerCase().indexOf(this.searchKeyword) > -1
          ) ||
          (
            x.name && x.name.toLowerCase().indexOf(this.searchKeyword) > -1
          )
        );
      }

      if (!this.loadMore && this.pageSize) {
        results = results.slice(0, this.pageSize)
      }

      return results;
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-console-element {
  .console-element-item-draggable.sortable-chosen {
    .console-element-item-child {
      display: none;
    }
  }
  .console-element-item {
    position: relative;
    padding: .75rem .375rem .75rem 1.375rem;
    cursor: pointer;
    &.console-element-item-child {
      padding-left: 2.5rem;
    }
    &:hover {
      @include theme() {
        background: rgba(theme-get('fts-gray-1'), .75);
      }
      .console-element-item__handle,
      .console-element-authorization .auth-toggle:not(.lock),
      .console-element-action-trigger,
      .console-element-automation-action {
        visibility: visible !important;
      }
    }
    .console-element-item__handle {
      @include absolute-center-y;
      left: .25rem;
      color: var(--fts-gray-5);
      font-size: 0.75rem;
      visibility: hidden;
      cursor: grab;
    }
    .console-element-item__content {
      display: flex;
      .console-element-item__content-wrapper {
        flex: 1;
        @include flex-gap($display: flex, $gap: .375rem);
        align-items: center;
      }
      .console-element-item__content-action {
        display: inline-flex;
        align-content: center;
        align-self: flex-start;
        margin-left: .5rem;
        & > *:not(:first-child) {
          margin-left: .375rem;
        }
      }
      .console-element-info {
        display: flex;
        flex-wrap: wrap;
        align-content: center;
      }
      .console-element-icon {
        margin-right: 0.575rem;
        .info-icon {
          &.console-element-icon--system {
            color: var(--fts-primary);
          }
          color: var(--fts-secondary);
          font-size: 0.875rem;
        }
      }
      .console-element-emoji {
        margin-right: 0.575rem;
      }
      .console-element-fa {
        margin-right: 0.575rem;
        font-size: 1.15rem;
      }
      .console-element-state > div {
        font-size: .875rem;
      }
      .console-element-flow {
        min-width: 0;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin: -0.175rem;
        cursor: pointer;
        .flow-state,
        .flow-activity,
        .flow-direction,
        .flow-conditional {
          margin: 0.175rem;
        }
        .flow-activity {
          min-width: 0;
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          .flow-activity-emoji {
            margin-right: 0.575rem;
          }
          .flow-activity-name {
            @include text-overflow-hidden;
            font-size: inherit;
            color: var(--fts-text);
          }
        }
        .flow-no-activity {
          color: var(--fts-gray-5);
          font-size: 0.875rem;
        }
        .flow-direction {
          color: var(--fts-gray-5);
        }
        .flow-conditional {
          color: var(--fts-gray-5);
          font-size: 0.875rem;
        }
      }
      .console-element-name {
        font-size: .875rem;
        line-height: 1.25rem;
        color: var(--fts-text);
      }
      .console-element-btn {
        min-height: auto;
        height: auto;
        padding: .25rem .5rem;
      }
      .console-element-badge {
        @include user-select-none;
        align-self: center;
        cursor: default;
        &.console-element-badge--icon {
          font-size: 1rem;
          line-height: 1rem;
          color: var(--fts-gray-5);
        }
        &:not(.console-element-badge--icon) {
          padding: 0 0.375rem;
          border-radius: 0.25rem;
          background-color: var(--fts-gray-2);
          color: var(--fts-gray-6);
          font-size: 0.75rem;
          line-height: 1rem;
        }
        &.console-element-badge--primary {
          &:not(.console-element-badge--icon) {
            @include theme() {
              background-color: rgba(theme-get('fts-primary'), .095);
            }
          }
          color: var(--fts-primary);
        }
      }
      .console-element-badge-tippy {
        align-self: center;
        .console-element-badge--icon-wrapper {
          display: flex;
          align-items: center;
        }
      }
      .console-element-list {
        min-width: 0;
        align-self: center;
        padding: 0 .375rem;
        border-radius: 0.25rem;
        background-color: var(--fts-gray-2);
        .console-element-list-container {
          @include flex-gap($display: flex, $gap: 0.175rem);
          .console-element-list-wrapper {
            min-width: 0;
            display: flex;
            align-items: center;
            color: var(--fts-gray-6);
            font-size: .75rem;
            line-height: 1rem;
          }
        }
      }
      .console-element-authorization {
        display: flex;
        align-items: center;
        .auth-toggle {
          font-size: .875rem;
          &:not(.lock) {
            visibility: hidden;
          }
        }
      }
      .console-element-action-trigger {
        color: var(--fts-gray-5);
        font-size: 0.875rem;
        cursor: pointer;
        visibility: hidden;
      }
      .console-element-automation-action {
        visibility: hidden;
      }
    }
    .console-element-item__automation {
      display: flex;
      align-items: center;
      .console-element-item__automation-container {
        @include flex-gap($display: flex, $gap: .375rem);
        padding-top: .375rem;
      }
    }
  }
  .console-element-footer {
    margin-top: .5rem;
    margin-left: 1rem;
    margin-right: 1rem;
  }
}
/* dropdown menu */
.ce-content.ce-dropdown-menu {
  min-width: 120px;
  .ce-action-option {
    .ce-action-option-icon {
      margin-right: 0.375rem;
      color: var(--fts-gray-5);
    }
  }
}
/* form preview */
.form-preview {
  max-height: 300px;
  width: 348px;
  display: flex;
  flex-direction: column;
  margin: -0.375rem -0.75rem;
  .form-preview__header {
    @include text-overflow-ellipsis;
    flex-shrink: 0;
    padding: .75rem;
    background-color: var(--fts-gray-2);
    font-size: .875rem;
    font-weight: 700;
  }
  .form-preview__content {
    padding-top: .375rem;
    padding-bottom: .375rem;
    .form-preview-information {
      display: flex;
      align-items: flex-start;
      padding: .375rem .75rem;
      font-size: .875rem;
      line-height: 1.25rem;
      .form-preview-information-icon {
        margin-right: .575rem;
        color: var(--fts-secondary);
        line-height: inherit;
      }
    }
    .form-preview-blank {
      padding: .375rem .75rem;
      font-size: .875rem;
      line-height: 1.25rem;
    }
  }
}
</style>